import { ExternalLink, Handshake, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { DeadlineBadge } from "./DeadlineBadge";
import { formatCop, isOverdue, isUrgent } from "./opportunitiesService";
import { SOURCE_BADGE, SOURCE_LABEL, TERMINAL_STATUSES, URGENT_DAYS } from "./opportunityConstants";
import type { Opportunity } from "./opportunityTypes";

interface OpportunityCardProps {
  opportunity: Opportunity;
  onOpen: (o: Opportunity) => void;
  onDragStart: (o: Opportunity) => void;
  onDragEnd: () => void;
  isDragging?: boolean;
}

export function OpportunityCard({
  opportunity,
  onOpen,
  onDragStart,
  onDragEnd,
  isDragging,
}: OpportunityCardProps) {
  const terminal = TERMINAL_STATUSES.includes(opportunity.status);
  const urgent = isUrgent(opportunity, URGENT_DAYS);
  const overdue = isOverdue(opportunity);

  return (
    <article
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", opportunity.id);
        onDragStart(opportunity);
      }}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(opportunity)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(opportunity);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${opportunity.title} — ${opportunity.status}`}
      className={cn(
        "cursor-pointer rounded-lg border bg-card p-3 text-left shadow-sm transition",
        "hover:border-primary/40 hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isDragging && "opacity-40",
        overdue && "border-red-300 dark:border-red-900",
        urgent && !overdue && "border-amber-300 dark:border-amber-900",
      )}
    >
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <span
          className={cn(
            "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
            SOURCE_BADGE[opportunity.source],
          )}
        >
          {SOURCE_LABEL[opportunity.source]}
        </span>
        {opportunity.fit_score ? (
          <span
            className="inline-flex shrink-0 items-center gap-0.5 text-xs text-amber-600 dark:text-amber-400"
            title={`Fit ${opportunity.fit_score}/5`}
          >
            <Star className="h-3 w-3 fill-current" />
            {opportunity.fit_score}
          </span>
        ) : null}
      </div>

      <h4 className="mb-1 line-clamp-2 text-sm font-medium leading-snug">
        {opportunity.title}
      </h4>

      {opportunity.entity && (
        <p className="mb-2 line-clamp-1 text-xs text-muted-foreground">{opportunity.entity}</p>
      )}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <DeadlineBadge deadline={opportunity.deadline} muted={terminal} />
        <span className="text-xs font-medium tabular-nums text-muted-foreground">
          {formatCop(opportunity.budget_cop)}
        </span>
        {opportunity.needs_partner && (
          <span
            className="inline-flex items-center gap-1 text-xs text-violet-600 dark:text-violet-400"
            title="Needs a prime partner or unión temporal"
          >
            <Handshake className="h-3 w-3" />
            UT
          </span>
        )}
        {opportunity.url && (
          <a
            href={opportunity.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="ml-auto text-muted-foreground hover:text-primary"
            aria-label="Open source notice"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </article>
  );
}
